import { Trans, useLingui } from '@lingui/react/macro';

import {
  useHasSubscription,
  useIntegrationProviders,
  useIntegrationTermsOfService,
  useIntegrations,
} from 'src/api/queries';
import { SomethingWentWrong, Spinner } from 'src/components';
import { AppPage } from 'src/components/AppPage';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { LinkExternal } from 'src/components/LinkExternal';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';
import { ConnectFeatureTag } from './components/ConnectFeatureTag';
import { IntegrationsFAQ } from './components/IntegrationsFAQ';
import { IntegrationsList } from './components/IntegrationsList';
import { ProviderList } from './components/ProviderList';
import { SaveIntegrationModal } from './components/SaveIntegrationModal';
import { TermsOfServiceForm } from './components/TermsOfServiceForm';

export const IntegrationsApp = () => {
  const { t } = useLingui();
  const integrations = useIntegrations();
  const providers = useIntegrationProviders();
  const tos = useIntegrationTermsOfService();
  const hasSubscription = useHasSubscription();

  const storageProvidersCount = providers.data?.filter(
    (p) => p.providerType === 'Storage'
  ).length;
  const messagingProvidersCount = providers.data?.filter(
    (p) => p.providerType === 'Messaging'
  ).length;
  const calendarProvidersCount = providers.data?.filter(
    (p) => p.providerType === 'Calendar'
  ).length;

  const meetingNotesTitle = t`Meeting Notes tool`;
  const storageTitle = t`Storage`;
  const sharingTitle = t({
    message: 'Sharing',
    comment: 'Integrations section title for messaging providers',
  });

  if (integrations.isError || providers.isError) {
    return <SomethingWentWrong error={integrations.error || providers.error} />;
  }

  if (integrations.isPending || providers.isPending) {
    return <Spinner />;
  }

  return (
    <AppPage.Container>
      <AppPage.Header
        className="max-w-l2xl"
        title={t`Integrations`}
        subtitle={t`Add your favorite tools to streamline your workflow.`}
      />
      <AppPage.Content className="max-w-l2xl gap-56">
        <TermsOfServiceForm />
        <IntegrationsList />
        {!!calendarProvidersCount && (
          <ProviderList
            title={meetingNotesTitle}
            tag={
              <ConnectFeatureTag
                hasSubscription={!!hasSubscription.data}
                isNew
              />
            }
            description={
              <Trans>
                Get meeting notes with key details from your calendar. See{' '}
                <LinkExternal
                  componentLocation={
                    ComponentLocations.INTEGRATIONS.PROVIDER_LIST
                  }
                  to={SUPPORT_URL.ARTICLES.HOW_TO_USE_MEETING_NOTES}
                  inline
                >
                  this article
                </LinkExternal>{' '}
                for more instructions.
              </Trans>
            }
            providerType="Calendar"
          />
        )}
        {!!storageProvidersCount && (
          <ProviderList
            title={storageTitle}
            description={
              <Trans>
                Easily access, sync, and store files with cloud storage services
                such as Google Drive, Dropbox, and OneDrive.
              </Trans>
            }
            providerType="Storage"
          />
        )}
        {!!messagingProvidersCount && (
          <ProviderList
            title={sharingTitle}
            tag={<ConnectFeatureTag hasSubscription={!!hasSubscription.data} />}
            providerType="Messaging"
          />
        )}

        {tos.hasAccepted && (
          <>
            <SaveIntegrationModal />
            <IntegrationsFAQ />
          </>
        )}
      </AppPage.Content>
    </AppPage.Container>
  );
};
